import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaBuilding, FaSearch, FaSpinner, FaTimes } from 'react-icons/fa';
import { addBuildingFromDirectory, fetchAvailableBuildings } from '../../../actions/infoTerminalApi';
import styles from './styles.module.scss';

const BuildingSelectModal = ({ isOpen, parentId, onClose }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  const { availableBuildings, availableBuildingsLoading, saving } = useSelector(
    (state) => state.infoTerminal
  );

  // Загружаем здания при открытии модалки
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchAvailableBuildings());
      setSearchTerm('');
      setSelectedBuilding(null);
    }
  }, [isOpen, dispatch]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Фильтрация зданий по поиску
  const filteredBuildings = useMemo(() => {
    if (!searchTerm.trim()) return availableBuildings;
    const term = searchTerm.toLowerCase();
    return availableBuildings.filter(
      (b) =>
        b.short_title?.toLowerCase().includes(term) ||
        b.full_title?.toLowerCase().includes(term) ||
        b.address?.toLowerCase().includes(term)
    );
  }, [availableBuildings, searchTerm]);

  const handleSelect = async () => {
    if (!selectedBuilding || saving) return;
    await dispatch(addBuildingFromDirectory(parentId, selectedBuilding));
    onClose();
  };

  const handleRowClick = (building) => {
    setSelectedBuilding(building);
  };

  const handleRowDoubleClick = (building) => {
    setSelectedBuilding(building);
    setTimeout(() => handleSelect(), 0);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span>
            <FaBuilding /> Выбор здания из справочника УМБ
          </span>
          <button className={styles.closeBtn} onClick={onClose} title="Закрыть">
            <FaTimes />
          </button>
        </div>

        <div className={styles.searchContainer}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Поиск по названию или адресу..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.modalContent}>
          {availableBuildingsLoading ? (
            <div className={styles.loadingState}>
              <FaSpinner className={styles.spinner} />
              <span>Загрузка справочника...</span>
            </div>
          ) : filteredBuildings.length === 0 ? (
            <div className={styles.emptyState}>
              {availableBuildings.length === 0
                ? 'Нет доступных зданий. Все здания уже добавлены в схему.'
                : 'Ничего не найдено по вашему запросу'}
            </div>
          ) : (
            <table className={styles.buildingsTable}>
              <thead>
                <tr>
                  <th>Наименование</th>
                  <th>Этажность</th>
                  <th>Адрес</th>
                </tr>
              </thead>
              <tbody>
                {filteredBuildings.map((building) => (
                  <tr
                    key={building.tfoid}
                    className={`${styles.buildingRow} ${
                      selectedBuilding?.tfoid === building.tfoid ? styles.selected : ''
                    }`}
                    onClick={() => handleRowClick(building)}
                    onDoubleClick={() => handleRowDoubleClick(building)}
                  >
                    <td>
                      <div className={styles.buildingName}>
                        <strong>{building.short_title}</strong>
                        {building.full_title && building.full_title !== building.short_title && (
                          <span className={styles.fullTitle}>{building.full_title}</span>
                        )}
                      </div>
                    </td>
                    <td className={styles.floorsCell}>
                      {building.floors_normalized || 1}
                    </td>
                    <td className={styles.addressCell}>{building.address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerInfo}>
            {selectedBuilding && (
              <span>
                Выбрано: <strong>{selectedBuilding.short_title}</strong>
                {selectedBuilding.floors_normalized > 0 &&
                  ` (${selectedBuilding.floors_normalized} этаж${
                    selectedBuilding.floors_normalized === 1
                      ? ''
                      : selectedBuilding.floors_normalized < 5
                      ? 'а'
                      : 'ей'
                  })`}
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={onClose}
              disabled={saving}
            >
              Отмена
            </button>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSelect}
              disabled={!selectedBuilding || saving}
            >
              {saving ? (
                <>
                  <FaSpinner className={styles.spinner} /> Добавление...
                </>
              ) : (
                'Добавить здание'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BuildingSelectModal;
